// cat > /mnt/user-data/outputs/Jammu.js << 'ENDOFFILE'
import React, { useState, useEffect, useRef, useCallback } from "react";

// ── Index configs ─────────────────────────────────────────────
const INDICES = {
  nifty:    { id: 13,  seg: "IDX_I", label: "NIFTY",    range: 500,  lotSize: 65,  futSeg: "NSE_FNO"  },
  sensex:   { id: 51,  seg: "IDX_I", label: "SENSEX",   range: 1000, lotSize: 20,  futSeg: "BSE_FNO"  },
  banknifty:{ id: 25,  seg: "IDX_I", label: "BANKNIFTY",range: 500,  lotSize: 30,  futSeg: "NSE_FNO"  },
  bankex:   { id: 69, seg: "IDX_I", label: "BANKEX",   range: 500,  lotSize: 30,  futSeg: "BSE_FNO"  },
};

const REFRESH_SEC = 60;
const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

// ── Styles ────────────────────────────────────────────────────
const C = {
  bg:     "#020812",
  panel:  "#050e1a",
  card:   "#070f1c",
  border: "#0c1f38",
  neon:   "#00ffe7",
  green:  "#00ff88",
  red:    "#ff3366",
  amber:  "#ffaa00",
  blue:   "#38bdf8",
  purple: "#a78bfa",
  pink:   "#f472b6",
  text:   "#b0c4de",
  muted:  "#2a3f5f",
  dim:    "#0f1e30",
};

const glowStyle = (color) => ({
  textShadow: `0 0 10px ${color}, 0 0 20px ${color}, 0 0 40px ${color}`,
  color,
});

const cardStyle = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: "14px 16px",
};

const labelStyle = {
  fontSize: 9,
  color: C.muted,
  letterSpacing: 3,
  textTransform: "uppercase",
  marginBottom: 4,
};

const inputStyle = {
  background: "#020c18",
  border: `1px solid ${C.muted}`,
  color: C.blue,
  padding: "7px 10px",
  borderRadius: 4,
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
  width: "100%",
};

const FONT = "'JetBrains Mono','Fira Code','Courier New',monospace";

// ── Safe fetch helper ─────────────────────────────────────────
async function sf(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.error) {
    throw new Error(json.error || `Request failed ${res.status}`);
  }

  return json;
}

// ── Black-Scholes Greeks (client-side) ───────────────────────
// Used when Dhan doesn't return greek fields
function normDist(x) {
  const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  const sign = x < 0 ? -1 : 1;
  const t = 1 / (1 + p * Math.abs(x) / Math.SQRT2);
  const y = 1 - (((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x/2);
  return 0.5 * (1 + sign * y);
}
function normPDF(x) { return Math.exp(-0.5*x*x) / Math.sqrt(2*Math.PI); }

// eslint-disable-next-line no-unused-vars
function bsGreeks(S, K, T, r, sigma, type) {
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) return { delta: 0, gamma: 0, iv: sigma * 100 };
  const d1 = (Math.log(S/K) + (r + 0.5*sigma*sigma)*T) / (sigma*Math.sqrt(T));
  // eslint-disable-next-line no-unused-vars
  const d2 = d1 - sigma*Math.sqrt(T);
  const delta = type === "CE" ? normDist(d1) : normDist(d1) - 1;
  const gamma = normPDF(d1) / (S * sigma * Math.sqrt(T));
  return { delta: Math.abs(delta), gamma, iv: sigma * 100 };
}

function normalizeParentOrder(order) {
  const txn = String(order.transactionType || order.txnType || order.type || "").toUpperCase();
  const status = String(order.orderStatus || order.status || "").toUpperCase();
  const optionType = getParentOrderOptionType(order);

  return {
    id: order.orderId || order.orderNo || order.exchangeOrderId || "",
    parentClientId: order.parentClientId || order.dhanClientId || order.clientId || "",
    txn: txn.startsWith("B") ? "BUY" : txn.startsWith("S") ? "SELL" : txn,
    status,
    optionType,
    symbol: order.tradingSymbol || order.symbol || order.displayName || order.securityId || "-",
    qty: Number(order.quantity || order.filledQty || order.remainingQuantity || 0),
    price: Number(order.price || order.orderPrice || order.averageTradedPrice || 0),
    time: order.createTime || order.updateTime || order.exchangeTime || order.orderDateTime || "",
  };
}

function getParentOrderOptionType(order) {
  const rawType = String(
    order.optionType ||
    order.drvOptionType ||
    order.optType ||
    order.option_type ||
    ""
  ).toUpperCase();

  if (rawType === "CE" || rawType.includes("CALL")) return "CE";
  if (rawType === "PE" || rawType.includes("PUT")) return "PE";

  const text = String(
    `${order.tradingSymbol || ""} ${order.symbol || ""} ${order.displayName || ""} ${order.instrument || ""}`
  ).toUpperCase();

  if (text.includes(" CALL") || text.includes(" CE") || text.endsWith("CE")) return "CE";
  if (text.includes(" PUT") || text.includes(" PE") || text.endsWith("PE")) return "PE";

  return "";
}

function isLiveParentOrder(order) {
  if (!["BUY", "SELL"].includes(order.txn)) return false;
  if (order.status.includes("REJECT") || order.status.includes("CANCEL")) return false;
  return true;
}

// eslint-disable-next-line no-unused-vars
function parentOrderSide(orders) {
  const liveOrders = orders.filter(isLiveParentOrder);

  if (!liveOrders.length) {
    return { side: "", liveOrders, buyQty: 0, sellQty: 0 };
  }

  const buyQty = liveOrders
    .filter(order => order.txn === "BUY")
    .reduce((sum, order) => sum + Math.max(order.qty, 1), 0);

  const sellQty = liveOrders
    .filter(order => order.txn === "SELL")
    .reduce((sum, order) => sum + Math.max(order.qty, 1), 0);

  if (buyQty > sellQty) return { side: "BUY", liveOrders, buyQty, sellQty };
  if (sellQty > buyQty) return { side: "SELL", liveOrders, buyQty, sellQty };

  const latest = [...liveOrders].sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0))[0];
  return { side: latest?.txn || "", liveOrders, buyQty, sellQty };
}

function latestParentBuyOptionSide(orders) {
  const validBuyOrders = orders
    .filter(order => order.txn === "BUY")
    .filter(order => !order.status.includes("REJECT") && !order.status.includes("CANCEL"))
    .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

  const latestBuy = validBuyOrders[0];

  if (!latestBuy) {
    return {
      side: "",
      orderType: "",
      latestBuy: null,
      liveOrders: [],
      buyQty: 0,
      sellQty: 0,
    };
  }

  const side =
    latestBuy.optionType === "CE"
      ? "BUY"
      : latestBuy.optionType === "PE"
      ? "SELL"
      : "";

  return {
    side,
    orderType: latestBuy.optionType,
    latestBuy,
    liveOrders: validBuyOrders,
    buyQty: latestBuy.txn === "BUY" ? Math.max(latestBuy.qty, 1) : 0,
    sellQty: 0,
  };
}

function getLoginUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function readLocalJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
}

async function loadCopyDataForJammu() {
  const token = localStorage.getItem(TOKEN_KEY);
  const loginUser = getLoginUser();
  let groups = [];
  let accounts = [];
  let source = "localStorage";

  if (token) {
    try {
      const [groupsRes, accountsRes] = await Promise.all([
        fetch(`${API_BASE}/api/login/data/groups`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/login/data/accounts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const groupsData = await groupsRes.json().catch(() => ({}));
      const accountsData = await accountsRes.json().catch(() => ({}));

      if (groupsRes.ok && groupsData.success) {
        groups = groupsData.groups || [];
        source = "backend";
      }

      if (accountsRes.ok && accountsData.success) {
        accounts = accountsData.accounts || [];
        source = "backend";
      }
    } catch (err) {
      console.log("Jammu copy data backend load failed:", err.message);
    }
  }

  if (!groups.length) {
    groups = [
      ...readLocalJson(loginUser?.id ? `ct_groups_${loginUser.id}` : "ct_groups_guest"),
      ...readLocalJson("ct_groups"),
    ];
  }

  if (!accounts.length) {
    accounts = [
      ...readLocalJson(loginUser?.id ? `ct_accounts_${loginUser.id}` : "ct_accounts_guest"),
      ...readLocalJson("ct_accounts"),
    ];
  }

  return { groups, accounts, source };
}

function getGroupParentAccounts(groups, accounts) {
  const activeGroups = (groups || []).filter(group => group?.parent && group.active !== false);
  const parentIds = [...new Set(activeGroups.map(group => String(group.parent)))];

  return parentIds
    .map(parentId => {
      const account = (accounts || []).find(acc =>
        String(acc.id) === parentId ||
        String(acc.clientId || "") === parentId ||
        String(acc.userId || "") === parentId
      );

      return account ? { ...account, parentId } : { id: parentId, parentId, missingAccount: true };
    });
}

// ── Main Component ────────────────────────────────────────────
export default function Jammu({ secretClientId = "", secretAccessToken = "", secretSpots = {} }) {

  const loginUser = getLoginUser();
  const userStoragePrefix = `secret_${loginUser?.id || loginUser?.username || "guest"}`;
  const storageKey = useCallback((key) => `${userStoragePrefix}_${key}`, [userStoragePrefix]);  

  // Credentials + spots
  const [creds, setCreds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey("jammu_creds")) || "{}"); } catch { return {}; }
  });
  const [spots, setSpots] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey("jammu_spots")) || "{}"); } catch { return {}; }
  });
  const [editCreds, setEditCreds] = useState(!creds.clientId);
  const [editSpots, setEditSpots] = useState(false);
  const [tempCreds, setTempCreds] = useState(creds);
  const [tempSpots, setTempSpots] = useState(spots);

  useEffect(() => {
    if (!secretClientId || !secretAccessToken) return;

    const nextCreds = {
      clientId: secretClientId,
      accessToken: secretAccessToken,
    };

    setCreds(nextCreds);
    setTempCreds(nextCreds);
    setEditCreds(false);
    localStorage.setItem(storageKey("jammu_creds"), JSON.stringify(nextCreds));
  }, [secretClientId, secretAccessToken, storageKey]);

  useEffect(() => {
    const secretSpotEntries = Object.fromEntries(
      Object.entries(secretSpots || {}).filter(([, value]) => value)
    );

    if (!Object.keys(secretSpotEntries).length) return;

    setSpots(prev => {
      const nextSpots = {
        ...prev,
        ...secretSpotEntries,
      };

      setTempSpots(nextSpots);
      localStorage.setItem(storageKey("jammu_spots"), JSON.stringify(nextSpots));
      return nextSpots;
    });
  }, [secretSpots, storageKey]);

  // Engine
  const [running,   setRunning]   = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const [error,     setError]     = useState("");
  const [data,      setData]      = useState(null);
  const [rangeData, setRangeData] = useState(null);
  const [activeJammuTab, setActiveJammuTab] = useState("main");
  const [greeksData, setGreeksData] = useState(null);
  const [greeksLoading, setGreeksLoading] = useState(false);
  const [greeksError, setGreeksError] = useState("");
  const greeksRef = useRef(null);
  const [orderAlignment, setOrderAlignment] = useState(null);
  const ivRef = useRef(null);
  const cdRef = useRef(null);
  const orderRef = useRef(null);

  const saveCreds = () => {
    localStorage.setItem(storageKey("jammu_creds"), JSON.stringify(tempCreds));
    setCreds(tempCreds);
    setEditCreds(false);
  };
  const saveSpots = () => {
    localStorage.setItem(storageKey("jammu_spots"), JSON.stringify(tempSpots));
    setSpots(tempSpots);
    setEditSpots(false);
  };

  // ── Fetch all data ──────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!creds.clientId || !creds.accessToken) {
      setError("Enter ClientID and Access Token first");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const auth = { accessToken: creds.accessToken, clientId: creds.clientId };
      try {
        const rangeRes = await sf(`${API_BASE}/api/jammu/range`, {
          ...auth,
          fallbackSpots: spots,
          indiaVix: spots.indiaVix,
        });
        setRangeData(rangeRes);
      } catch (e) {
        setRangeData({ error: e.message });
      }

      const result = {};

      // Fetch all indices together
const entries = Object.entries(INDICES);

for (const [key, idx] of entries) {
  const spot = parseFloat(spots[key] || 0);

  if (!spot) {
    result[key] = null;
    continue;
  }

  try {
    const expData = await sf(`${API_BASE}/api/options/expiry`, {
      ...auth,
      underlyingId: idx.id,
      segment: idx.seg,
    });

    if (!expData.expiry) {
      result[key] = null;
      continue;
    }

    const chainData = await sf(`${API_BASE}/api/jammu/chain`, {
      ...auth,
      underlyingId: idx.id,
      segment: idx.seg,
      expiry: expData.expiry,
      spotPrice: spot,
      range: idx.range,
      allExpiries: expData.allExpiries || [],
    });

    result[key] = chainData;
  } catch (e) {
    result[key] = { error: e.message };
  }

  await new Promise(resolve => setTimeout(resolve, 1200));
}


      setData(result);
      setCountdown(REFRESH_SEC);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [creds, spots]);

  const start = async () => {
    setRunning(true);
    localStorage.setItem(storageKey("jammu_running"), "true");
    await fetchAll();
    ivRef.current = setInterval(fetchAll, REFRESH_SEC * 1000);
    cdRef.current = setInterval(() =>
      setCountdown(c => c <= 1 ? REFRESH_SEC : c - 1)
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    clearInterval(orderRef.current);
    clearInterval(greeksRef.current);
    greeksRef.current = null;
    localStorage.removeItem(storageKey("jammu_running"));
    ivRef.current = null; cdRef.current = null;
    orderRef.current = null;
    setRunning(false); setCountdown(REFRESH_SEC);
  };

  const fetchGreeks = useCallback(async () => {
  if (!creds.clientId || !creds.accessToken) {
    setGreeksError("Enter ClientID and Access Token first");
    return;
  }

  setGreeksLoading(true);
  setGreeksError("");

  try {
    const auth = { accessToken: creds.accessToken, clientId: creds.clientId };

    const liveRange = await sf(`${API_BASE}/api/jammu/range`, {
      ...auth,
      fallbackSpots: spots,
      indiaVix: spots.indiaVix,
    });

    setRangeData(liveRange);

    const out = {};

    for (const [key, idx] of Object.entries(INDICES)) {
      const liveSpot = Number(liveRange?.ranges?.[key]?.spot || spots[key] || 0);
      if (!liveSpot) continue;

      const expData = await sf(`${API_BASE}/api/options/expiry`, {
        ...auth,
        underlyingId: idx.id,
        segment: idx.seg,
      });

      if (!expData.expiry) continue;

      out[key] = await sf(`${API_BASE}/api/jammu/chain`, {
        ...auth,
        underlyingId: idx.id,
        segment: idx.seg,
        expiry: expData.expiry,
        spotPrice: liveSpot,
        range: idx.range,
        allExpiries: expData.allExpiries || [],
      });

      await new Promise(resolve => setTimeout(resolve, 800));
    }

    setGreeksData(out);
  } catch (e) {
    setGreeksError(e.message || "Failed to fetch Greeks");
  } finally {
    setGreeksLoading(false);
  }
}, [creds, spots]);

  const startGreeks = async () => {
  await fetchGreeks();
  clearInterval(greeksRef.current);
  greeksRef.current = setInterval(fetchGreeks, REFRESH_SEC * 1000);
};

const stopGreeks = () => {
  clearInterval(greeksRef.current);
  greeksRef.current = null;
};

  useEffect(() => () => { clearInterval(ivRef.current); clearInterval(cdRef.current); clearInterval(orderRef.current); }, []);

  // ── Signal computation ────────────────────────────────────────
  const signal = React.useMemo(() => {
    if (!data) return null;
    let bullish = 0, bearish = 0;

    for (const key of ["nifty", "sensex", "banknifty"]) {
      const d = data[key];
      if (!d || d.error) continue;
      // ITM Volume: more CE vol = bullish, more PE vol = bearish
      if (d.itm?.ceVolume > d.itm?.peVolume) bullish++;
      else if (d.itm?.peVolume > d.itm?.ceVolume) bearish++;
      // OI: more put sellers = bullish, more call sellers = bearish
      if (d.oi?.totalPeOI > d.oi?.totalCeOI) bullish++;
      else if (d.oi?.totalCeOI > d.oi?.totalPeOI) bearish++;
      // PCR
      const pcr = d.oi?.pcr || 0;
      if (pcr > 1.2) bullish++;
      else if (pcr < 0.8) bearish++;
      // Futures: buyers > sellers = bullish
      if (d.futures?.length > 0) {
        const f = d.futures[0];
        if (f.buyers > f.sellers) bullish++;
        else if (f.sellers > f.buyers) bearish++;
      }
    }

    if (bullish > bearish + 1) return { label: "BUY", color: C.green, sub: "BULLISH" };
    if (bearish > bullish + 1) return { label: "SELL", color: C.red, sub: "BEARISH" };
    return { label: "NEUTRAL", color: C.amber, sub: "WAIT" };
  }, [data]);

  const analyseParentOrders = useCallback(async () => {
    if (!signal) return;

    try {
      const { groups, accounts, source } = await loadCopyDataForJammu();
      const parentAccounts = getGroupParentAccounts(groups, accounts);
      const parentIds = parentAccounts.map(acc => String(acc.parentId || acc.id || acc.clientId || acc.userId || ""));
      const usableParents = parentAccounts.filter(acc =>
        !acc.missingAccount &&
        acc.accessToken &&
        (!acc.broker || String(acc.broker).toLowerCase() === "dhan")
      );
      const marketSide = signal.label === "BUY" || signal.label === "SELL" ? signal.label : "";

      if (!parentAccounts.length) {
        setOrderAlignment({
          status: "no-parent",
          color: C.amber,
          headline: "NO GROUP PARENT ACCOUNT FOUND",
          message: "Create a group in Groups page and select the parent account. Jammu will then monitor that parent ID orders.",
          marketSide: marketSide || "WAIT",
          orderSide: "NONE",
          liveCount: 0,
          parentIds: [],
          dataSource: source,
          checkedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        });
        return;
      }

      if (!usableParents.length) {
        setOrderAlignment({
          status: "parent-token-missing",
          color: C.amber,
          headline: "GROUP PARENT ACCOUNT TOKEN NOT FOUND",
          message: `Parent ID ${parentIds.join(", ")} is selected in Groups, but its Dhan access token was not found in Accounts page.`,
          marketSide: marketSide || "WAIT",
          orderSide: "NONE",
          liveCount: 0,
          parentIds,
          dataSource: source,
          checkedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        });
        return;
      }

      const ordersByParent = await Promise.all(
        usableParents.map(async (parent) => {
          const parentClientId = parent.clientId || parent.id || parent.userId || parent.parentId;

          const res = await fetch(`${API_BASE}/api/orders/dhan`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              clientId: parentClientId,
              accessToken: parent.accessToken,
            }),
          });

          const raw = await res.json().catch(() => ({}));
          return (raw.orders || []).map(order => ({
            ...order,
            parentClientId,
          }));
        })
      );

      const normalized = ordersByParent.flat().map(normalizeParentOrder);
      const orderBias = latestParentBuyOptionSide(normalized);

      if (!marketSide) {
        setOrderAlignment({
          status: "neutral",
          color: C.amber,
          headline: "MARKET SIGNAL IS NEUTRAL",
          message: "Latest parent BUY option order is being watched, but Jammu data is currently in WAIT mode.",
          marketSide: "WAIT",
          orderSide: orderBias.side ? `${orderBias.side} (${orderBias.orderType} BUY)` : "NONE",
          liveCount: orderBias.liveOrders.length,
          recentOrder: orderBias.latestBuy ? `${orderBias.latestBuy.symbol} ${orderBias.orderType || ""}` : "",
          parentIds,
          dataSource: source,
          checkedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        });
        return;
      }

      if (!orderBias.side) {
        const hasLatestBuy = !!orderBias.latestBuy;

        setOrderAlignment({
          status: "waiting",
          color: C.amber,
          headline: hasLatestBuy
            ? "RECENT PARENT BUY OPTION TYPE NOT FOUND"
            : "NO RECENT PARENT BUY OPTION FOUND",
          message: hasLatestBuy
            ? `Market data points to ${marketSide}. Latest parent BUY order ${orderBias.latestBuy.symbol} does not show CE/PE, so Jammu cannot confirm direction.`
            : `Market data points to ${marketSide}. Parent ID ${parentIds.join(", ")} has no recent CE/PE BUY order to compare right now.`,
          marketSide,
          orderSide: hasLatestBuy ? "UNKNOWN BUY" : "NONE",
          liveCount: orderBias.liveOrders.length,
          recentOrder: hasLatestBuy ? `${orderBias.latestBuy.symbol} ${orderBias.orderType || ""}` : "",
          parentIds,
          dataSource: source,
          checkedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        });
        return;
      }

      const aligned = orderBias.side === marketSide;

      setOrderAlignment({
        status: aligned ? "aligned" : "against",
        color: aligned ? C.green : C.red,
        headline: aligned
          ? "YOUR ORDERS ARE WITH THE MARKET"
          : "YOUR PARENT ACCOUNT ORDERS ARE AGAINST THE MARKET",
        message: aligned
          ? `Latest parent ${orderBias.orderType} BUY order is aligned with ${marketSide}. Be confident and hold.`
          : `Latest parent ${orderBias.orderType} BUY order is against ${marketSide}. Kindly reverse the direction as per the data.`,
        marketSide,
        orderSide: `${orderBias.side} (${orderBias.orderType} BUY)`,
        liveCount: orderBias.liveOrders.length,
        buyQty: orderBias.buyQty,
        sellQty: orderBias.sellQty,
        recentOrder: orderBias.latestBuy ? `${orderBias.latestBuy.symbol} ${orderBias.orderType || ""}` : "",
        parentIds,
        dataSource: source,
        checkedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      });
    } catch (err) {
      setOrderAlignment({
        status: "error",
        color: C.amber,
        headline: "PARENT ORDER CHECK FAILED",
        message: err.message || "Unable to fetch parent orders right now.",
        marketSide: signal?.label || "WAIT",
        orderSide: "UNKNOWN",
        parentIds: [],
      });
    }
  }, [signal]);

  useEffect(() => {
    if (!running || !signal) return;

    analyseParentOrders();
    clearInterval(orderRef.current);
    orderRef.current = setInterval(analyseParentOrders, 5000);

    return () => clearInterval(orderRef.current);
  }, [running, signal, analyseParentOrders]);

  const fmtOI  = v => v != null ? Number(v).toLocaleString("en-IN") : "—";
  // eslint-disable-next-line no-unused-vars
  const fmtVol = v => v != null ? (v / 1e7).toFixed(2) + " Cr" : "—";
  const fmtN   = (v, d=2) => v != null ? Number(v).toFixed(d) : "—";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: FONT, padding: "0 0 40px 0" }}>

      {/* ── TOP BAR ── */}
      <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "stretch", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0" }}>
          <span style={{ ...glowStyle(C.neon), fontSize: 16, fontWeight: "bold", letterSpacing: 4 }}>⬡ JAMMU SCANNER</span>
          <span style={{ fontSize: 8, color: C.muted, letterSpacing: 3 }}>INSTITUTIONAL MARKET INTELLIGENCE</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
          {running && <span style={{ fontSize: 10, color: C.green, letterSpacing: 2 }}>● LIVE — {countdown}s</span>}
          {loading  && <span style={{ fontSize: 10, color: C.amber }}>⟳ FETCHING...</span>}
          {error    && <span style={{ fontSize: 10, color: C.red }}>⚠ {error}</span>}
        </div>
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>

        {/* ── CREDENTIALS + SPOTS ROW ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

          {/* Credentials */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ ...labelStyle, marginBottom: 0 }}>CREDENTIALS</div>
              <button onClick={() => editCreds ? saveCreds() : setEditCreds(true)}
                style={{ background: editCreds ? C.green+"20" : C.muted+"20", border: `1px solid ${editCreds ? C.green : C.muted}`, color: editCreds ? C.green : C.text, padding: "3px 12px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontFamily: FONT }}>
                {editCreds ? "SAVE" : "EDIT"}
              </button>
            </div>
            {editCreds ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input placeholder="Client ID" value={tempCreds.clientId || ""} onChange={e => setTempCreds(p => ({ ...p, clientId: e.target.value }))} style={inputStyle} />
                <input placeholder="Access Token" type="password" value={tempCreds.accessToken || ""} onChange={e => setTempCreds(p => ({ ...p, accessToken: e.target.value }))} style={inputStyle} />
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.blue }}>
                <div>Client: <span style={{ color: C.neon }}>{creds.clientId || "—"}</span></div>
                <div>Token: <span style={{ color: C.muted }}>{"•".repeat(20)}</span></div>
              </div>
            )}
          </div>

          {/* Spot prices */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ ...labelStyle, marginBottom: 0 }}>SPOT PRICES</div>
              <button onClick={() => editSpots ? saveSpots() : setEditSpots(true)}
                style={{ background: editSpots ? C.green+"20" : C.muted+"20", border: `1px solid ${editSpots ? C.green : C.muted}`, color: editSpots ? C.green : C.text, padding: "3px 12px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontFamily: FONT }}>
                {editSpots ? "SAVE" : "EDIT"}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {Object.entries(INDICES).map(([key, idx]) => (
                <div key={key}>
                  <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 2 }}>{idx.label}</div>
                  {editSpots
                    ? <input type="number" value={tempSpots[key] || ""} onChange={e => setTempSpots(p => ({ ...p, [key]: e.target.value }))} style={{ ...inputStyle, width: "80%" }} placeholder={idx.label + " spot"} />
                    : <span style={{ color: C.blue, fontSize: 14, fontWeight: "bold" }}>{spots[key] || "—"}</span>
                  }
                </div>
              ))}

              <div>
                <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 2 }}>INDIA VIX</div>
                {editSpots
                  ? <input type="number" value={tempSpots.indiaVix || ""} onChange={e => setTempSpots(p => ({ ...p, indiaVix: e.target.value }))} style={{ ...inputStyle, width: "80%" }} placeholder="India VIX" />
                  : <span style={{ color: C.amber, fontSize: 14, fontWeight: "bold" }}>{spots.indiaVix || "—"}</span>
                }
            </div>

            </div>
          </div>
        </div>

        {/* ── START / STOP ── */}
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
          {!running
            ? <button onClick={start} disabled={loading || !creds.clientId}
                style={{ background: "linear-gradient(135deg,#005c2e,#00ff88)", border: "none", color: "#000", padding: "12px 40px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: "bold", letterSpacing: 3, boxShadow: `0 0 20px ${C.green}60` }}>
                ▶ START ALL SCANNERS
              </button>
            : <button onClick={stop}
                style={{ background: "linear-gradient(135deg,#5c0015,#ff3366)", border: "none", color: "#fff", padding: "12px 40px", borderRadius: 6, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: "bold", letterSpacing: 3, boxShadow: `0 0 20px ${C.red}60` }}>
                ■ STOP
              </button>
          }
          
          <button
  onClick={fetchAll}
  disabled={loading || !creds.clientId || !creds.accessToken}
  style={{
    background: "linear-gradient(135deg,#0f2040,#38bdf8)",
    border: "none",
    color: "#fff",
    padding: "12px 28px",
    borderRadius: 6,
    cursor: loading ? "not-allowed" : "pointer",
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: "bold",
    letterSpacing: 2,
    boxShadow: `0 0 14px ${C.blue}50`,
    opacity: loading ? 0.6 : 1,
  }}
>
  REFRESH NOW
</button>


          {running && <div style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>AUTO REFRESH EVERY {REFRESH_SEC}s</div>}
        </div>

        {/* ── SIGNAL ── */}
        {orderAlignment && (
          <div
            style={{
              ...cardStyle,
              borderColor: orderAlignment.color + "80",
              marginBottom: 18,
              textAlign: "center",
              padding: "22px 20px",
              boxShadow: `0 0 24px ${orderAlignment.color}30`,
            }}
          >
            <div style={{ fontSize: 9, color: C.muted, letterSpacing: 4, marginBottom: 8 }}>
              PARENT ORDER ALIGNMENT CHECK - EVERY 5 SECONDS
            </div>

            <div
              style={{
                ...glowStyle(orderAlignment.color),
                fontSize: 30,
                fontWeight: "bold",
                letterSpacing: 3,
                lineHeight: 1.2,
              }}
            >
              {orderAlignment.headline}
            </div>

            <div style={{ fontSize: 15, color: C.text, marginTop: 10 }}>
              {orderAlignment.message}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 18,
                flexWrap: "wrap",
                marginTop: 14,
                fontSize: 10,
                letterSpacing: 2,
                color: C.muted,
              }}
            >
              <span>MARKET: <b style={{ color: orderAlignment.color }}>{orderAlignment.marketSide}</b></span>
              <span>PARENT ORDERS: <b style={{ color: orderAlignment.color }}>{orderAlignment.orderSide}</b></span>
              {orderAlignment.parentIds?.length > 0 && <span>PARENT ID: <b style={{ color: C.neon }}>{orderAlignment.parentIds.join(", ")}</b></span>}
              {orderAlignment.recentOrder && <span>RECENT BUY: <b style={{ color: C.blue }}>{orderAlignment.recentOrder}</b></span>}
              <span>BUY ORDERS CHECKED: <b style={{ color: C.blue }}>{orderAlignment.liveCount ?? 0}</b></span>
              {orderAlignment.dataSource && <span>GROUP SOURCE: <b style={{ color: C.purple }}>{orderAlignment.dataSource}</b></span>}
              {orderAlignment.checkedAt && <span>CHECKED: <b style={{ color: C.neon }}>{orderAlignment.checkedAt}</b></span>}
            </div>
          </div>
        )}

        {signal && (
          <div style={{ ...cardStyle, borderColor: signal.color + "60", marginBottom: 24, textAlign: "center", padding: "28px 20px" }}>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 4, marginBottom: 8 }}>AGGREGATE MARKET SIGNAL</div>
            <div style={{ ...glowStyle(signal.color), fontSize: 72, fontWeight: "bold", lineHeight: 1, letterSpacing: 8 }}>
              {signal.label}
            </div>
            <div style={{ fontSize: 18, color: signal.color, letterSpacing: 6, marginTop: 8 }}>{signal.sub}</div>
          </div>
        )}

        {!data && !loading && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.muted }}>
            <div style={{ fontSize: 56, marginBottom: 16, color: C.neon, opacity: 0.2 }}>⬡</div>
            <div style={{ fontSize: 12, letterSpacing: 4 }}>ENTER CREDENTIALS, SPOT PRICES AND PRESS START</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <button onClick={() => setActiveJammuTab("main")} style={{ padding: "8px 18px", borderRadius: 4, border: `1px solid ${activeJammuTab === "main" ? C.neon : C.border}`, background: C.card, color: activeJammuTab === "main" ? C.neon : C.text, fontFamily: FONT }}>
            MAIN DATA
          </button>
          <button onClick={() => setActiveJammuTab("greeks")} style={{ padding: "8px 18px", borderRadius: 4, border: `1px solid ${activeJammuTab === "greeks" ? C.amber : C.border}`, background: C.card, color: activeJammuTab === "greeks" ? C.amber : C.text, fontFamily: FONT }}>
            GREEKS
          </button>
        </div>

        {/* ── PCR COLUMN + INDEX GRIDS ── */}
        {activeJammuTab === "main" && data && (
          <>
            {/* ── ROW 1: ITM VOLUME + OI SELLERS ── */}
            <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
              SECTION 1 — DAY / WEEKLY RANGE
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              <RangePanel label="NIFTY" d={rangeData?.ranges?.nifty} color={C.blue} />
              <RangePanel label="SENSEX" d={rangeData?.ranges?.sensex} color={C.purple} />
              <RangePanel label="BANKNIFTY" d={rangeData?.ranges?.banknifty} color={C.pink} />
            </div>

            {/* ── ROW 2: FUTURES SUMMARY ── */}
            <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
              SECTION 2 — FUTURES SUMMARY (3 MONTHS)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <FuturesSummary label="NIFTY" d={data.nifty} color={C.blue} fmtOI={fmtOI} />
              <FuturesSummary label="BANKNIFTY" d={data.banknifty} color={C.pink} fmtOI={fmtOI} />
            </div>

            {/* ── ROW 3: GREEKS STRIKES ── */}
            {false && (
            <>
              {/* ── ROW 3: GREEKS STRIKES ── */}
              <div style={{ marginBottom: 8, fontSize: 9, color: C.neon, letterSpacing: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
                SECTION 3 — NEAREST STRIKES (Δ≥0.40 · Γ≥0.0012 · IV≥15%)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <GreeksPanel label="NIFTY"     d={data.nifty}     color={C.blue}   fmtN={fmtN} />
                <GreeksPanel label="SENSEX"    d={data.sensex}    color={C.purple} fmtN={fmtN} />
                <GreeksPanel label="BANKNIFTY" d={data.banknifty} color={C.pink}   fmtN={fmtN} />
                <GreeksPanel label="BANKEX"    d={data.bankex}    color={C.amber}  fmtN={fmtN} />
              </div>
            </>
          )}
          </>
        )}

        {activeJammuTab === "greeks" && (
          <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
              <button onClick={startGreeks} disabled={greeksLoading} style={{ background: C.amber, color: "#000", border: "none", padding: "9px 22px", borderRadius: 4, fontWeight: "bold", fontFamily: FONT }}>
                {greeksLoading ? "FETCHING..." : "FETCH / SCAN GREEKS"}
              </button>

              <button onClick={stopGreeks} style={{ background: C.red, color: "#fff", border: "none", padding: "9px 22px", borderRadius: 4, fontWeight: "bold", fontFamily: FONT }}>
                STOP GREEKS
              </button>

              {greeksError && <span style={{ color: C.red, fontSize: 11 }}>{greeksError}</span>}
            </div>

            {greeksData && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <GreeksPanel label="NIFTY" d={greeksData.nifty} color={C.blue} fmtN={fmtN} />
                <GreeksPanel label="SENSEX" d={greeksData.sensex} color={C.purple} fmtN={fmtN} />
                <GreeksPanel label="BANKNIFTY" d={greeksData.banknifty} color={C.pink} fmtN={fmtN} />
                <GreeksPanel label="BANKEX" d={greeksData.bankex} color={C.amber} fmtN={fmtN} />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function RangePanel({ label, d, color }) {
  if (!d) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2 }}>{label}</div>
        <div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No range data</div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} RANGE
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <Stat label="LIVE SPOT" value={d.spot} color={C.blue} />
        <Stat label="INDIA VIX" value={d.indiaVix} color={C.amber} />
      </div>

      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
        RANGE: {d.rangePercent}% · {d.rangePoints} pts
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Stat label="SUPPORT" value={d.support} color={C.green} />
        <Stat label="RESISTANCE" value={d.resistance} color={C.red} />
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function PCRBadge({ label, d }) {
  if (!d || d.error) return <div style={{ ...cardStyle, padding: "8px", textAlign: "center" }}><div style={{ fontSize: 8, color: "#2a3f5f" }}>{label}</div><div style={{ color: "#2a3f5f" }}>—</div></div>;
  const pcr = d.oi?.pcr || 0;
  const color = pcr > 1.2 ? "#00ff88" : pcr < 0.8 ? "#ff3366" : "#ffaa00";
  return (
    <div style={{ ...cardStyle, padding: "8px", textAlign: "center", borderColor: color + "40" }}>
      <div style={{ fontSize: 7, color: "#2a3f5f", letterSpacing: 2, marginBottom: 2 }}>{label} PCR</div>
      <div style={{ fontSize: 16, fontWeight: "bold", color, textShadow: `0 0 8px ${color}` }}>{pcr.toFixed(2)}</div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function IndexVolumeOI({ label, d, color, fmtVol, fmtOI }) {
  if (!d) return <div style={{ ...cardStyle, opacity: 0.3 }}><div style={{ fontSize: 10, color }}>{label}</div><div style={{ color: "#2a3f5f", fontSize: 11 }}>No spot price entered</div></div>;
  if (d.error) return <div style={{ ...cardStyle }}><div style={{ fontSize: 10, color }}>{label}</div><div style={{ color: "#ff3366", fontSize: 11 }}>⚠ {d.error}</div></div>;

  const itm = d.itm || {};
  const oi  = d.oi  || {};

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label}
      </div>

      {/* ITM Volume */}
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>ITM VOLUME (TOP 10)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <Stat label="CE VOLUME" value={fmtVol(itm.ceVolume)} color="#00ff88" />
        <Stat label="PE VOLUME" value={fmtVol(itm.peVolume)} color="#ff3366" />
      </div>

      {/* OI Sellers */}
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>CALL & PUT SELLERS OI</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <Stat label="CALL SELLERS OI" value={fmtOI(oi.totalCeOI)} color="#ff3366" />
        <Stat label="PUT SELLERS OI"  value={fmtOI(oi.totalPeOI)} color="#00ff88" />
      </div>

      {/* OI bar */}
      {(oi.totalCeOI || oi.totalPeOI) ? (() => {
        const total = (oi.totalCeOI || 0) + (oi.totalPeOI || 0);
        const ceP = total > 0 ? ((oi.totalCeOI / total) * 100).toFixed(1) : 50;
        return (
          <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: "hidden", display: "flex" }}>
            <div style={{ width: `${ceP}%`, background: "linear-gradient(90deg,#b91c1c,#ff3366)", transition: "width 0.8s" }} />
            <div style={{ flex: 1, background: "linear-gradient(90deg,#15803d,#00ff88)" }} />
          </div>
        );
      })() : null}
    </div>
  );
}

function FuturesSummary({ label, d, color, fmtOI }) {
  if (!d || d.error || !d.futures?.length)
    return <div style={cardStyle}><div style={{ fontSize: 11, color }}>{label} FUTURES</div><div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No futures data</div></div>;

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} FUTURES SUMMARY
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr style={{ background: C.dim }}>
            {["EXPIRY", "LTP", "BUYERS (OI)", "SELLERS (OI)", "BIAS"].map(h => (
              <th key={h} style={{ padding: "6px 10px", textAlign: "right", color: C.muted, fontSize: 8, letterSpacing: 1, fontWeight: "normal" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.futures.map((f, i) => {
            const bias = f.buyers > f.sellers ? { label: "BULL", color: "#00ff88" } : f.sellers > f.buyers ? { label: "BEAR", color: "#ff3366" } : { label: "NEUT", color: "#ffaa00" };
            return (
              <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: "7px 10px", color: C.text }}>{f.expiry}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: C.blue }}>₹{Number(f.ltp || 0).toFixed(2)}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: "#00ff88" }}>{fmtOI(f.buyers)}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", color: "#ff3366" }}>{fmtOI(f.sellers)}</td>
                <td style={{ padding: "7px 10px", textAlign: "right" }}>
                  <span style={{ color: bias.color, fontSize: 9, fontWeight: "bold", border: `1px solid ${bias.color}40`, padding: "1px 6px", borderRadius: 3 }}>{bias.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GreeksPanel({ label, d, color, fmtN }) {
  if (!d || d.error || !d.greeks)
    return <div style={cardStyle}><div style={{ fontSize: 11, color }}>{label} GREEKS</div><div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>No data</div></div>;

  const { ce, pe } = d.greeks;

  const StrikeCard = ({ s, type }) => {
    if (!s) return <div style={{ ...cardStyle, background: C.dim, opacity: 0.4, padding: "10px 12px" }}><div style={{ color: C.muted, fontSize: 10 }}>No qualifying strike</div></div>;
    const c = type === "CE" ? "#00ff88" : "#ff3366";
    return (
      <div style={{ ...cardStyle, borderColor: c + "30", padding: "10px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ color: c, fontWeight: "bold", fontSize: 12 }}>{s.strike} {type}</span>
          <span style={{ color: C.blue, fontSize: 12 }}>₹{fmtN(s.ltp)}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
          <MiniStat label="Δ DELTA" value={fmtN(s.delta, 4)} color={c} />
          <MiniStat label="Γ GAMMA" value={fmtN(s.gamma, 4)} color={c} />
          <MiniStat label="IV %" value={fmtN(s.iv, 1) + "%"} color={C.amber} />
        </div>
      </div>
    );
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 11, color, fontWeight: "bold", letterSpacing: 2, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
        {label} — QUALIFYING STRIKES
      </div>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>CALL SIDE (CE)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <StrikeCard s={ce?.[0]} type="CE" />
        <StrikeCard s={ce?.[1]} type="CE" />
      </div>
      <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>PUT SIDE (PE)</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <StrikeCard s={pe?.[0]} type="PE" />
        <StrikeCard s={pe?.[1]} type="PE" />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: "bold", color }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ background: C.dim, borderRadius: 4, padding: "4px 6px" }}>
      <div style={{ fontSize: 7, color: C.muted, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 11, color, fontWeight: "bold" }}>{value}</div>
    </div>
  );
}
//ENDOFFILE
